import { useCallback, useEffect, useMemo, useState } from "react";

import {
  scheduleMaxDateTime,
  scheduleMinDateTime,
  setActiveScheduleTimezone,
  toScheduledDate,
} from "./scheduleDateTime";

const browserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

interface UseScheduleFieldParams {
  isOpen: boolean;
  armed: boolean;
  value: string;
  setArmed: (armed: boolean) => void;
  setValue: (value: string) => void;
}

export interface ScheduleField {
  armed: boolean;
  value: string;
  hasValue: boolean;
  isIncomplete: boolean;
  timezone: string;
  setTimezone: (timezone: string) => void;
  minDateTime: string;
  maxDateTime: string;
  toggle: () => void;
  getScheduledDate: () => Date | null;
}

export const useScheduleField = ({
  isOpen,
  armed,
  value,
  setArmed,
  setValue,
}: UseScheduleFieldParams): ScheduleField => {
  const [timezone, setTimezone] = useState<string>(browserTimeZone);

  useEffect(() => {
    setActiveScheduleTimezone(timezone);
    return () => setActiveScheduleTimezone(undefined);
  }, [timezone]);

  const minDateTime = useMemo(
    () => scheduleMinDateTime(timezone),
    [isOpen, timezone]
  );
  const maxDateTime = useMemo(
    () => scheduleMaxDateTime(timezone),
    [isOpen, timezone]
  );

  const hasValue = Boolean(value && value.trim().length);
  const isIncomplete = armed && !hasValue;

  const toggle = useCallback(() => {
    const next = !armed;
    setArmed(next);
    if (!next) setValue("");
  }, [armed, setArmed, setValue]);

  const getScheduledDate = useCallback(
    () => (armed && hasValue ? toScheduledDate(value, timezone) : null),
    [armed, hasValue, value, timezone]
  );

  return {
    armed,
    value,
    hasValue,
    isIncomplete,
    timezone,
    setTimezone,
    minDateTime,
    maxDateTime,
    toggle,
    getScheduledDate,
  };
};
